import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Button } from 'src/common/components/button';
import { useOpenModal } from 'src/common/components/next-modal';
import { getActivity } from 'src/hooks/activities/use-activity';
import { createActivityModalAnchor } from '../activities/create-activity-modal-anchor';
import { FeatGuideTaskName } from 'src/hooks/activities/activity-ids';
import { useOpenOneTaskModal } from './task-progress';
import { publicLink } from 'src/common/const';
import { Images } from 'src/image';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

interface Props {
  onCloseModal: () => void;
  resolve: (value: unknown) => void;
  taskId: string;
}

enum Stage {
  One,
  Two,
  Three,
}

export const FeatGuideStep: FC<Props> = ({ resolve, onCloseModal, taskId }) => {
  const { t } = useAppTranslation();
  const [stage, setStage] = useState(Stage.One);

  const activity = getActivity(taskId);
  const [currentIndex, setIndex] = useState(0);
  if (!activity) return null;

  const resourceList = activity.resourceList ?? [];
  const handleNext = () => {
    if (currentIndex < resourceList.length - 1) {
      setIndex(currentIndex + 1);
    } else {
      setStage(Stage.Three);
    }
  };

  if (stage === Stage.One) {
    return (
      <div className="next-modal w-[360px] rounded p-[30px]">
        <div className="text-t1-medium">
          {t('components.new_user_task.feat_guide.learn_feature', {
            feature: FeatGuideTaskName[taskId],
          })}
        </div>
        <div className="flex items-center mt-[30px]">
          <Button
            colorType="black"
            onClick={() => {
              onCloseModal();
              resolve(0);
            }}
            className="border-grey5"
          >
            {t('common.self_explore')}
          </Button>
          <Button colorType="active" className="ml-2.5" onClick={() => setStage(Stage.Two)}>
            {t('components.new_user_task.feat_guide.take_action')}
          </Button>
        </div>
      </div>
    );
  }

  if (stage === Stage.Two) {
    const imgInfo = activity.resourceList?.[currentIndex];
    return (
      <div className="next-modal w-[320px] rounded p-[30px]">
        <p className="text-t1-medium">{FeatGuideTaskName[taskId]}</p>
        <p className="text-t2 mt-1">{imgInfo?.content}</p>
        <img src={imgInfo?.imgUrl} className="mt-2.5" alt="" />

        <div className="flex items-center mt-[30px]">
          <Button
            colorType="black"
            onClick={() => setIndex(currentIndex - 1)}
            disable={currentIndex === 0}
            className="border-grey5"
          >
            {t('components.new_user_task.feat_guide.previous_step')}
          </Button>
          <Button colorType="active" className="ml-2.5" onClick={handleNext}>
            {currentIndex === resourceList.length - 1 ? t('common.done') : t('common.next_step')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="next-modal w-[320px] rounded p-[30px]">
      <p className="text-t1-medium">{t('components.new_user_task.feat_guide.learn_more_flowus')}</p>
      <p className="text-t2 mt-1">{t('components.new_user_task.feat_guide.visit_help_center')}</p>
      <img src={Images.activityHelp} className="mt-2.5" alt="" />

      <div className="flex items-center mt-[30px]">
        <Button colorType="black" onClick={onCloseModal} className="border-grey5">
          {t('components.new_user_task.feat_guide.later')}
        </Button>
        <Button
          colorType="active"
          className="ml-2.5"
          onClick={() => {
            onCloseModal();
            window.open(publicLink.helpCenter);
          }}
        >
          {t('common.go_now')}
        </Button>
      </div>
    </div>
  );
};

export const useOpenFeatGuide = () => {
  const openModal = useOpenModal();
  const openOneTaskModal = useOpenOneTaskModal();

  return useCallback(
    (taskId: string, isFirstTask?: boolean) => {
      return new Promise((resolve) => {
        const activityModalAnchor = createActivityModalAnchor();

        openModal.dropdown({
          popcorn: activityModalAnchor.popcorn,
          placement: 'top-start',
          offset: [-50, 0],
          closeAfterCallBack: async () => {
            activityModalAnchor.remove();

            if (isFirstTask) {
              await openOneTaskModal(taskId);
            }
            resolve(0);
          },
          content: ({ onCloseModal }) => {
            return <FeatGuideStep onCloseModal={onCloseModal} taskId={taskId} resolve={resolve} />;
          },
        });
      }).catch((err) => {
        console.warn(err);
      });
    },
    [openModal, openOneTaskModal]
  );
};
